/**
 * PayPal Button Component
 * Uses PayPal Hosted Buttons â€” simplest & most reliable integration.
 */

import { useEffect, useRef, useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'

// Hosted Button IDs â€” baked in at build time via Vite env vars
const HOSTED_BUTTON_IDS = {
  start: {
    monthly: import.meta.env.VITE_PAYPAL_HOSTED_BUTTON_START_MONTHLY,
    annual:  import.meta.env.VITE_PAYPAL_HOSTED_BUTTON_START_ANNUAL,
  },
  pro: {
    monthly: import.meta.env.VITE_PAYPAL_HOSTED_BUTTON_PRO_MONTHLY,
    annual:  import.meta.env.VITE_PAYPAL_HOSTED_BUTTON_PRO_ANNUAL,
  }
}

const CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID

// Module-level SDK load promise â€” reset on failure so retries work
let sdkPromise = null

function loadSDK() {
  if (sdkPromise) return sdkPromise

  sdkPromise = new Promise((resolve, reject) => {
    // Already loaded with HostedButtons support
    if (window.paypal?.HostedButtons) { resolve(); return }

    // Remove any old SDK scripts that used different params
    document.querySelectorAll('script[src*="paypal.com/sdk"]').forEach(s => s.remove())
    if (window.paypal) { try { delete window.paypal } catch (_) { window.paypal = undefined } }

    const s = document.createElement('script')
    s.src = `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}&components=hosted-buttons&disable-funding=venmo&currency=USD`
    s.crossOrigin = 'anonymous'
    s.async = true
    s.onload = () => {
      // Small delay â€” PayPal SDK needs a tick to register HostedButtons
      setTimeout(resolve, 100)
    }
    s.onerror = (e) => {
      sdkPromise = null  // Allow retry
      reject(new Error('PayPal SDK failed to load'))
    }
    document.body.appendChild(s)
  })

  return sdkPromise
}

export default function PayPalButton({
  planName,
  billingCycle = 'monthly',
  amount,
  onSuccess,
  onError
}) {
  // Stable container ID â€” must not change between renders
  const idRef = useRef(`pp-${planName}-${billingCycle}-${Math.random().toString(36).slice(2, 8)}`)
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'error'
  const [errorMsg, setErrorMsg] = useState('')
  const cleanupRef = useRef(null)

  useEffect(() => {
    let alive = true

    const run = async () => {
      setStatus('loading')
      setErrorMsg('')

      // 1. Validate config
      const buttonId = HOSTED_BUTTON_IDS[planName?.toLowerCase()]?.[billingCycle]

      if (!CLIENT_ID) {
        setErrorMsg('VITE_PAYPAL_CLIENT_ID missing â€” check Netlify env vars')
        setStatus('error')
        return
      }
      if (!buttonId) {
        setErrorMsg(`Button ID missing for ${planName}/${billingCycle} â€” check Netlify env vars`)
        setStatus('error')
        return
      }

      // 2. Load SDK
      try {
        await loadSDK()
      } catch (err) {
        if (!alive) return
        console.error('PayPal SDK load failed:', err)
        setErrorMsg('Could not load PayPal. Check your connection and refresh.')
        setStatus('error')
        if (onError) onError(err)
        return
      }

      if (!alive) return

      // 3. Verify HostedButtons is available
      if (!window.paypal?.HostedButtons) {
        setErrorMsg('PayPal HostedButtons API not available. Try refreshing.')
        setStatus('error')
        return
      }

      // 4. Clear container & render â€” show container BEFORE render so PayPal can measure it
      const container = document.getElementById(idRef.current)
      if (!container) return
      container.innerHTML = ''
      setStatus('ready')  // Make container visible BEFORE render call

      // Small tick so the DOM updates and container has dimensions
      await new Promise(r => setTimeout(r, 50))
      if (!alive) return

      try {
        const btn = window.paypal.HostedButtons({
          hostedButtonId: buttonId,
          onApprove: async (data) => {
            try {
              const details = {
                subscriptionID: data.subscriptionID || null,
                orderID:        data.orderID        || null,
                planName,
                billingCycle,
                amount,
                payer: {
                  payer_id:      data.payerID || null,
                  email_address: data.paymentSource?.paypal?.email_address || null
                }
              }
              if (onSuccess) onSuccess(details)
            } catch (err) {
              console.error('onApprove error:', err)
              if (onError) onError(err)
            }
          }
        })

        cleanupRef.current = async () => {
          try { await btn.close?.() } catch (_) {}
          const c = document.getElementById(idRef.current)
          if (c) c.innerHTML = ''
        }

        await btn.render(`#${idRef.current}`)
      } catch (err) {
        if (!alive) return
        console.error('PayPal render error:', err)
        // Don't show error UI â€” PayPal sometimes throws but still renders
        // Only show error if container is still empty after render attempt
        setTimeout(() => {
          if (!alive) return
          const c = document.getElementById(idRef.current)
          if (c && !c.hasChildNodes()) {
            setErrorMsg('PayPal button failed to render. Please refresh.')
            setStatus('error')
          }
        }, 1000)
        if (onError) onError(err)
      }
    }

    run()

    return () => {
      alive = false
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [planName, billingCycle])

  if (status === 'error') {
    return (
      <div className="w-full">
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-600 dark:text-red-400">
            Payment unavailable. Please try again or contact support.
          </p>
        </div>
        {import.meta.env.DEV && (
          <p className="text-[10px] text-red-400 mt-1 font-mono break-all">{errorMsg}</p>
        )}
      </div>
    )
  }

  return (
    <div className="w-full">
      {status === 'loading' && (
        <div className="flex justify-center items-center py-5">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      )}
      {/* Container must always be in DOM so PayPal can find it */}
      <div
        id={idRef.current}
          style={{
            minHeight: status === 'ready' ? '45px' : '0',
            minWidth: 250,
            display: 'block',
            overflow: 'visible'
          }}
      />
      {status === 'ready' && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          {billingCycle === 'annual' ? 'Annual' : 'Monthly'} subscription · Cancel anytime
        </p>
      )}
    </div>
  )
}
